/**
 * @file     MyReadPanelClient.tsx
 * @purpose  "How this price compares": the listing's asking-price signals as
 *           one ListingSection. A Buyer / Investor SegmentedControl on the
 *           heading row reweights the score; a soft band carries the value
 *           signal, the score and the scored date; each scored signal is a
 *           MeterRow; a disclosure lists every signal with its weight and
 *           the reason a missing one is not counted. Everything renders on the
 *           server pass and stays visible; lens switches swap text in place.
 *           @/services/deal-finder/engine/clientReweight, @/services/deal-finder/types
 */
'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { reltaTrack } from '@/hooks/useReltaTrack';
import { SegmentedControl } from '@/components/hub/SegmentedControl';
import { MeterRow, type MeterTone } from '@/components/hub/MeterRow';
import { HubActionButton } from '@/components/hub/HubActionButton';
import { hairline, hubColors } from '@/components/hub/hubKitStyles';
import { PremiumPanelGate } from '@/components/auth/PremiumPanelGate';
import { motionTransition } from '@/core/motion';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { ANOMALY_CATALOG } from '@/services/deal-finder/engine/anomalies';
import { reweight, PROFILE_WEIGHTS, type ClientProfileId } from '@/services/deal-finder/engine/clientReweight';
import type { ScoreBreakdown } from '@/services/deal-finder/types';
import { SubmitOfferAttemptModal } from './SubmitOfferAttemptModal';
import type { PanelPremiumConfig } from './panelRegistry';
import { ListingSection } from './ListingSection';
import { contributionPts, lensScoreInterval, trustLineText } from './myReadDisplay';
import { formatMonthDay } from '@/utils/dateFormat';
import { formatCADCents } from '@/utils/finance/mortgage';
import { usePanelImpression } from '@/hooks/usePanelImpression';
import { useAnalytics } from '@/hooks/useAnalytics';
import { analyticsEvents } from '@/core/constants/analytics';

type Bucket = 'smoking_hot' | 'worth_a_look' | 'fair_value' | 'skip';

interface RentalYield {
  grossYieldPct: number;
  netYieldPct: number | null;
  medianRent2br: number;
  areaLabel: string;
  assumptions: {
    vacancyPct: number;
    associationFee: number | null;
    taxAnnualAmount: number | null;
  };
  /** 'estimate' = area median / census-derived rent, not a verified figure. */
  precision: 'estimate' | 'verified';
}

interface NhpiPoint { period: string; value: number }

interface BenchmarkSeries {
  areaName: string;
  propertyType: string;
  points: NhpiPoint[];
}

export type MyReadPremiumGate = PanelPremiumConfig;

interface Props {
  initialBucket: Bucket;
  initialScore: number;
  initialConfidence: number;
  breakdown: ScoreBreakdown;
  anomalyFlags: string[];
  rentalYield: RentalYield | null;
  benchmarkSeries: BenchmarkSeries | null;
  bedroomsTotal: number | null;
  scoredAtIso: string;
  initialLens: ClientProfileId;
  alreadyAttempted: boolean;
  listingKey: string;
  listingPrice: number | null;
  listingAddress?: string | null;
  listingSlug?: string | null;
  /** Anchor id of the section. */
  sectionId?: string;
  /** Soft paywall around the body; the heading and lens control stay readable. */
  premium?: MyReadPremiumGate;
  /** 'h3' in a quick view, where the listing heading is the h2. */
  headingLevel?: 'h2' | 'h3';
}

const PROFILE_OPTIONS: ReadonlyArray<{ value: ClientProfileId; label: string }> = [
  { value: 'buyer', label: 'Buyer' },
  { value: 'investor', label: 'Investor' },
];

// Benchmark-relative, signal-framed labels. Never a verdict on the listing
// itself ("overpriced"): these describe what the signals show, which is safe
// to publish against another brokerage's listing.
const BUCKET_LABEL: Record<Bucket, string> = {
  smoking_hot: 'Strong value signals',
  worth_a_look: 'Good value signals',
  fair_value: 'In line with market',
  skip: 'Weak value signals',
};

// The weakest verdict wears neutral ink: never the brand colour (reads as an
// endorsement) and never alarm red (reads as disparagement).
const BUCKET_DOT: Record<Bucket, string> = {
  smoking_hot: hubColors.statusSuccess,
  worth_a_look: hubColors.statusSuccess,
  fair_value: hubColors.textSecondary,
  skip: hubColors.textMuted,
};

const COMPONENT_LABEL: Record<keyof ScoreBreakdown, string> = {
  priceVsComps: 'Price vs nearby asking',
  priceHistory: 'Price history',
  hpiDeviation: 'Vs area benchmark',
  marketContext: 'Market temperature',
  carryingCost: 'Carrying cost',
  transitAccess: 'Transit access',
  schoolQuality: 'School quality',
  hazardRisk: 'Hazard exposure',
  crimeDensity: 'Crime density',
};

const VISIBLE_COMPONENTS: Array<keyof ScoreBreakdown> = [
  'priceVsComps',
  'priceHistory',
  'hpiDeviation',
  'marketContext',
  'carryingCost',
  'transitAccess',
  'schoolQuality',
  'hazardRisk',
  'crimeDensity',
];

// Plain-language text per anomaly flag. Labels come from the engine's
// ANOMALY_CATALOG so the two surfaces cannot drift.
const ANOMALY_EXPLANATION: Record<string, string> = {
  comp_set_thin: 'Fewer than 3 similar listings nearby. Treat this signal with care.',
  comp_set_stale: 'Most nearby comparable listings are older than 60 days.',
  hpi_missing: 'No GVR HPI benchmark covers this area and property type.',
  price_history_cold: 'This listing is new. Its price history is still building.',
  incomplete_listing: 'Key listing fields (size or year built) are missing, which limits scoring.',
  carrying_cost_partial: 'Only one of strata fee and property tax is on the listing.',
  price_anchoring: 'The price sits just under a round number, a common pricing tactic.',
  stale_listing: 'This listing has been on the market well past the area norm.',
  outlier_pricing: 'Price per sqft is far outside the area range; treat the score with care.',
};

function meterTone(score: number): MeterTone {
  return score >= 65 ? 'positive' : 'neutral';
}

export function MyReadPanelClient({
  breakdown,
  anomalyFlags,
  rentalYield,
  bedroomsTotal,
  scoredAtIso,
  initialLens,
  alreadyAttempted,
  listingKey,
  listingPrice,
  listingAddress,
  listingSlug,
  sectionId = 'price',
  premium,
  headingLevel = 'h2',
}: Props) {
  const { t } = useTranslation();
  const [lens, setLens] = React.useState<ClientProfileId>(initialLens);
  const [attempted, setAttempted] = React.useState(alreadyAttempted);
  const [open, setOpen] = React.useState(false);
  const [offerOpen, setOfferOpen] = React.useState(false);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const breakdownId = React.useId();
  const { trackEvent } = useAnalytics();
  usePanelImpression(panelRef, analyticsEvents.propertyMyReadViewed, { metadata: { listing_key: listingKey, initial_lens: initialLens } });

  // Every lens scores from the same breakdown the panel displays, so the
  // score can never disagree with the visible signals.
  const lensed = React.useMemo(() => {
    const { score, bucket } = reweight(breakdown, lens);
    return { score, bucket: bucket as Bucket };
  }, [lens, breakdown]);
  // The rent behind the yield is an area 2BR median; it never headlines larger homes.
  const yieldForLens = lens === 'investor' && (bedroomsTotal == null || bedroomsTotal <= 2) ? rentalYield : null;
  const interval = React.useMemo(
    () => lensScoreInterval(breakdown, PROFILE_WEIGHTS[lens], VISIBLE_COMPONENTS, lensed.score),
    [breakdown, lens, lensed.score],
  );

  const ranked = VISIBLE_COMPONENTS
    .map((k) => ({ k, weight: PROFILE_WEIGHTS[lens][k] ?? 0, score: breakdown[k]?.score ?? null }))
    .sort((a, b) => Number(b.score != null) - Number(a.score != null) || b.weight - a.weight);
  const scored = ranked.filter((r) => r.score != null && r.weight > 0);
  const scoredLabel = formatMonthDay(scoredAtIso);

  const lensControl = (
    <SegmentedControl<ClientProfileId>
      size="sm"
      options={PROFILE_OPTIONS.map((o) => ({ ...o, label: t(`listingDetail.price.lens.${o.value}`, { defaultValue: o.label }) }))}
      value={lens}
      onChange={(next) => {
        setLens(next);
        trackEvent({
          eventName: analyticsEvents.propertyMyReadLensSwitched,
          eventType: 'click',
          category: 'property_detail_panel',
          label: next,
          metadata: { listing_key: listingKey, from: lens, to: next },
        });
        reltaTrack('panel.my_read.lens_switched', { listing_key: listingKey, from: lens, to: next });
      }}
      ariaLabel={t('listingDetail.price.lensLabel', { defaultValue: 'Switch profile lens' })}
    />
  );

  const body = (
    <Box ref={panelRef}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 4,
          rowGap: 2,
          px: 4,
          py: 3,
          borderRadius: `${layoutTokens.radius.lg}px`,
          backgroundColor: hubColors.surfaceSunken,
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            minHeight: 32,
            px: 3,
            borderRadius: `${layoutTokens.radius.pill}px`,
            backgroundColor: hubColors.surfaceCard,
            fontSize: 14,
            fontWeight: fontWeightTokens.medium,
            color: hubColors.textPrimary,
          }}
        >
          <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: BUCKET_DOT[lensed.bucket] }} />
          {t(`listingDetail.price.bucket.${lensed.bucket}`, { defaultValue: BUCKET_LABEL[lensed.bucket] })}
        </Box>
        <Box component="span" aria-live="polite" sx={{ fontSize: 14, color: hubColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
          {t('listingDetail.price.score', { defaultValue: 'Deal score {{score}} of 100', score: lensed.score })}
          {scoredLabel ? ` · ${t('listingDetail.price.scoredAt', { defaultValue: 'Scored {{date}}', date: scoredLabel })}` : ''}
        </Box>
        <Box component="span" sx={{ fontSize: 13, color: hubColors.textMuted, flexBasis: { xs: '100%', md: 'auto' } }}>
          {trustLineText(breakdown, VISIBLE_COMPONENTS, interval)}
        </Box>
      </Box>

      {yieldForLens ? (
        <Box component="p" sx={{ m: 0, mt: 4, fontSize: 15, lineHeight: 1.55, color: hubColors.textSecondary, maxWidth: '68ch' }}>
          <Box component="strong" sx={{ fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary }}>
            {yieldHeadline(yieldForLens)}.
          </Box>{' '}
          {yieldDetail(yieldForLens)}
        </Box>
      ) : null}

      {scored.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          {scored.map(({ k, score }) => (
            <MeterRow
              key={k}
              label={t(`listingDetail.price.signal.${k}`, { defaultValue: COMPONENT_LABEL[k] })}
              value={score ?? 0}
              valueText={sanitize(breakdown[k]?.explanation) ?? scoreVerdict(score ?? 0)}
              tone={meterTone(score ?? 0)}
            />
          ))}
        </Box>
      ) : null}

      <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
        <Box
          component="button"
          type="button"
          aria-expanded={open}
          aria-controls={breakdownId}
          onClick={() => {
            const next = !open;
            setOpen(next);
            if (next) {
              trackEvent({
                eventName: analyticsEvents.propertyMyReadBreakdownExpanded,
                eventType: 'click',
                category: 'property_detail_panel',
                label: 'expand_breakdown',
                metadata: { listing_key: listingKey, lens },
              });
              reltaTrack('panel.my_read.breakdown_expanded', { listing_key: listingKey });
            }
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            minHeight: 44,
            p: 0,
            border: 0,
            background: 'none',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: 15,
            fontWeight: fontWeightTokens.medium,
            color: hubColors.link,
            '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2, borderRadius: '4px' },
          }}
        >
          {open
            ? t('listingDetail.price.hideSignals', { defaultValue: 'Hide how it is scored' })
            : t('listingDetail.price.showSignals', { defaultValue: 'See how it is scored' })}
          <Box
            component="svg"
            aria-hidden
            viewBox="0 0 24 24"
            width={16}
            height={16}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            sx={{
              transform: open ? 'rotate(180deg)' : 'none',
              transition: motionTransition('transform', 'base'),
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            <path d="m6 9 6 6 6-6" />
          </Box>
        </Box>
        {attempted ? (
          <Box component="span" role="status" sx={{ fontSize: 14, fontWeight: fontWeightTokens.medium, color: hubColors.textSecondary }}>
            {t('listingDetail.price.offerSent', { defaultValue: 'Offer attempt submitted' })}
          </Box>
        ) : (
          <HubActionButton
            variant="secondary"
            action={{
              label: t('listingDetail.price.offerCta', { defaultValue: 'Start an offer' }),
              onClick: () => {
                setOfferOpen(true);
                trackEvent({
                  eventName: analyticsEvents.propertyOfferAttemptOpened,
                  eventType: 'click',
                  category: 'property_detail_panel',
                  label: 'submit_offer_attempt_cta',
                  metadata: { listing_key: listingKey, list_price: listingPrice, lens },
                });
              },
            }}
          />
        )}
      </Box>

      <Box id={breakdownId} hidden={!open} sx={{ mt: 2 }}>
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {ranked.map(({ k, weight, score }) => {
            const contribution = score != null ? contributionPts(score, weight) : null;
            const line = score != null ? sanitize(breakdown[k]?.explanation) : unavailableReason(k, anomalyFlags);
            return (
              <Box component="li" key={`${lens}-${k}`} sx={{ py: 3, borderBottom: hairline }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 3 }}>
                  <Box component="span" sx={{ fontSize: 15, fontWeight: fontWeightTokens.medium, color: score != null ? hubColors.textPrimary : hubColors.textSecondary }}>
                    {t(`listingDetail.price.signal.${k}`, { defaultValue: COMPONENT_LABEL[k] })}
                  </Box>
                  <Box component="span" sx={{ fontSize: 13, color: hubColors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                    {contribution != null
                      ? t('listingDetail.price.weightCounted', {
                          defaultValue: '{{weight}}% weight · {{points}} pts',
                          weight: (weight * 100).toFixed(0),
                          points: contribution.toFixed(1),
                        })
                      : t('listingDetail.price.weightMissing', { defaultValue: '{{weight}}% weight · not counted', weight: (weight * 100).toFixed(0) })}
                  </Box>
                </Box>
                {line ? (
                  <Box component="p" sx={{ m: 0, mt: 1, fontSize: 14, lineHeight: 1.5, color: hubColors.textSecondary, maxWidth: '68ch' }}>
                    {line}
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Box>
        {anomalyFlags.length > 0 ? (
          <Box component="ul" sx={{ m: 0, mt: 3, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {anomalyFlags.map((f) => {
              const entry = ANOMALY_CATALOG[f as keyof typeof ANOMALY_CATALOG];
              return (
                <Box component="li" key={f} sx={{ fontSize: 13, lineHeight: 1.5, color: hubColors.textSecondary }}>
                  <Box component="span" sx={{ fontWeight: fontWeightTokens.medium, color: hubColors.textPrimary }}>{entry?.label ?? f}.</Box>{' '}
                  {ANOMALY_EXPLANATION[f] ?? ''}
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Box>

      <Box component="p" sx={{ m: 0, mt: 4, fontSize: 13, lineHeight: 1.55, color: hubColors.textSecondary, maxWidth: '72ch' }}>
        {t('pages.propertyDetail.dealContext.disclaimer', {
          defaultValue: 'Based on nearby asking prices, price changes, area benchmarks and carrying cost. Use it as one input.',
        })}{' '}
        {t('listingDetail.price.sources', {
          defaultValue: 'Sources: BCRES listing feed, GVR MLS® HPI, TransLink, BC Geographic Warehouse, Vancouver Open Data, Statistics Canada (2021 Census).',
        })}
      </Box>

      <SubmitOfferAttemptModal
        open={offerOpen}
        onClose={() => setOfferOpen(false)}
        onSubmitted={() => setAttempted(true)}
        listingKey={listingKey}
        listingPrice={listingPrice}
        listingAddress={listingAddress}
        listingSlug={listingSlug}
      />
    </Box>
  );

  return (
    <ListingSection
      id={sectionId}
      anchorLabel={t('listingDetail.anchors.price', { defaultValue: 'Price' })}
      eyebrow={t('listingDetail.price.eyebrow', { defaultValue: 'Asking-price facts only' })}
      title={t('listingDetail.price.title', { defaultValue: 'How this price compares' })}
      action={lensControl}
      headingLevel={headingLevel}
    >
      {premium ? (
        <PremiumPanelGate
          panel={premium.panel}
          listingKey={listingKey}
          teaserTitle={premium.teaserTitle}
          teaserBody={premium.teaserBody}
          treatment={premium.treatment}
          analyticsSurface="slug_page"
        >
          {body}
        </PremiumPanelGate>
      ) : (
        body
      )}
    </ListingSection>
  );
}

function yieldHeadline(rentalYield: RentalYield): string {
  return rentalYield.netYieldPct != null
    ? `${rentalYield.netYieldPct.toFixed(1)}% estimated net yield`
    : `${rentalYield.grossYieldPct.toFixed(1)}% estimated gross yield`;
}

function yieldDetail(rentalYield: RentalYield): string {
  const bits: string[] = [];
  if (rentalYield.assumptions.associationFee && rentalYield.assumptions.associationFee > 0) bits.push(`strata ${formatCADCents(rentalYield.assumptions.associationFee)}/mo`);
  if (rentalYield.assumptions.taxAnnualAmount && rentalYield.assumptions.taxAnnualAmount > 0) bits.push(`tax ${formatCADCents(rentalYield.assumptions.taxAnnualAmount)}/yr`);
  const after = bits.length ? ` after ${Math.round(rentalYield.assumptions.vacancyPct * 100)}% vacancy, ${bits.join(' + ')}` : '';
  const estimateNote = rentalYield.precision === 'verified' ? '' : ' The rent is an area estimate.';
  return `Assumes ${formatPrice(rentalYield.medianRent2br)}/mo for a 2-bedroom in ${rentalYield.areaLabel}${after}. Leaves out insurance, upkeep and management.${estimateNote}`;
}

function formatPrice(p: number): string {
  return p.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });
}

function sanitize(s: string | undefined | null): string | null {
  if (!s) return null;
  // Older stored explanations may carry em dashes; a comma keeps the sentence readable.
  return s.replace(/\s*—\s*/g, ', ').trim();
}

function scoreVerdict(score: number): string {
  if (score >= 75) return 'Strong';
  if (score >= 55) return 'Normal';
  return 'Weak';
}

function unavailableReason(key: keyof ScoreBreakdown, anomalyFlags: string[]): string {
  switch (key) {
    case 'priceVsComps':
      if (anomalyFlags.includes('comp_set_thin')) return 'Too few comparable listings nearby to score this signal.';
      if (anomalyFlags.includes('comp_set_stale')) return 'Available comps are too old to score reliably.';
      return 'Not enough comparable listings nearby to score this signal.';
    case 'hpiDeviation':
      if (anomalyFlags.includes('hpi_missing')) return 'GVR HPI does not cover this area, so no benchmark is available.';
      return 'No area benchmark available for this listing.';
    case 'priceHistory':
      return 'Not enough listing history yet to score price moves.';
    case 'marketContext':
      return 'Sales-to-active activity for the area is not available yet.';
    case 'carryingCost':
      return 'Strata and tax fields are missing on this listing.';
    case 'transitAccess':
      return 'Transit stop data is still ingesting for this area.';
    case 'schoolQuality':
      return 'School catchment data is still ingesting for this area.';
    case 'hazardRisk':
      return 'Hazard map data is still ingesting for this area.';
    case 'crimeDensity':
      return 'Crime density data is still ingesting for this area.';
    default:
      return 'Data not available for this signal.';
  }
}
